import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Store, Package, Truck, DollarSign, Upload, Zap, MapPin, Gift, UserPlus, Users, Trash2, AlertTriangle, MessageSquare, Box, Minus, Pencil, Clock, Ban, Bookmark, Trophy, ExternalLink, BellRing } from "lucide-react";
import { api, apiError, fileUrl, trackingUrl } from "@/lib/api";
import { Loader } from "@/components/common";

const inp = "w-full bg-vault border border-gold/20 rounded-xl px-4 py-2.5 text-white placeholder-slate-500 focus:border-gold focus:outline-none text-sm";
const money = (n) => `${n < 0 ? "-" : ""}$${Math.abs(n).toFixed(2)}`;

function PayoutEstimate({ price, seats, free }) {
  const p = parseFloat(price) || 0, total = parseInt(seats) || 0;
  const paid = Math.max(total - (parseInt(free) || 0), 0);
  const gross = paid * p;
  const fees = paid > 0 && p > 0 ? paid * (p * 0.029 + 0.3) : 0;
  const net = gross - fees;
  return (
    <div className="rounded-xl border border-emerald-400/25 bg-emerald-500/5 p-4 space-y-2" data-testid="payout-estimate">
      <p className="text-[10px] font-mono font-bold uppercase tracking-wide text-emerald-300">What you'll receive · host only</p>
      <div className="flex justify-between text-xs"><span className="text-slate-400">{paid} paid seat{paid === 1 ? "" : "s"} × {money(p)}</span><span className="font-mono text-slate-300" data-testid="payout-gross">{money(gross)}</span></div>
      <div className="flex justify-between text-xs"><span className="text-slate-400">Stripe fees (2.9% + 30¢ each)</span><span className="font-mono text-red-300" data-testid="payout-fees">-{money(fees)}</span></div>
      <div className="flex justify-between items-baseline border-t border-emerald-400/20 pt-2">
        <span className="text-sm text-white font-semibold">You get</span>
        <span className="font-mono text-lg font-bold text-emerald-300" data-testid="payout-net">{money(net)}</span>
      </div>
      <p className="text-[10px] text-slate-500">When every seat sells, each as its own card checkout. Buyers pay only the seat price. Seats bought with store credit have no Stripe fee.</p>
    </div>
  );
}
const SHOP_BRANDS = ["PSA", "Beckett", "TAG", "Degree", "AGS", "CGC"];
const TABS = [
  { key: "drop", label: "New Drop", icon: Zap },
  { key: "shop", label: "New Shop Item", icon: Store },
  { key: "case", label: "New Case", icon: Box },
  { key: "orders", label: "Orders", icon: Package },
  { key: "credit", label: "Grant Credit", icon: DollarSign },
  { key: "manage", label: "Manage", icon: Trash2 },
];

function ImageUpload({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const up = async (file) => {
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/uploads", fd); onChange(data.path); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <label className="block cursor-pointer">
      <div className="aspect-video rounded-xl border-2 border-dashed border-gold/30 bg-vault flex items-center justify-center overflow-hidden hover:border-gold/60 transition-colors">
        {value ? <img src={fileUrl(value)} alt="" className="w-full h-full object-cover" /> : <div className="text-center text-slate-500"><Upload className="w-7 h-7 mx-auto mb-1" /><p className="text-xs">{busy ? "Uploading…" : "Upload photo"}</p></div>}
      </div>
      <input type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && up(e.target.files[0])} data-testid="image-upload-input" />
    </label>
  );
}

function MultiImageUpload({ values = [], onChange }) {
  const [busy, setBusy] = useState(false);
  const up = async (files) => {
    setBusy(true);
    try {
      const uploaded = [];
      for (const file of Array.from(files)) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/uploads", fd);
        uploaded.push(data.path);
      }
      onChange([...values, ...uploaded]);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const removeAt = (i) => onChange(values.filter((_, idx) => idx !== i));
  const makeCover = (i) => { if (i === 0) return; const arr = [...values]; const [p] = arr.splice(i, 1); arr.unshift(p); onChange(arr); };
  return (
    <div data-testid="multi-image-upload">
      <div className="grid grid-cols-3 gap-2">
        {values.map((v, i) => (
          <div key={v} className="relative aspect-square rounded-xl overflow-hidden border border-gold/20 group" data-testid={`shop-photo-${i}`}>
            <img src={fileUrl(v)} alt="" className="w-full h-full object-cover" />
            {i === 0 && <span className="absolute top-1 left-1 text-[9px] font-bold uppercase tracking-wide bg-gold text-obsidian px-1.5 py-0.5 rounded">Cover</span>}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              {i !== 0 && <button type="button" onClick={() => makeCover(i)} title="Make cover" className="text-[10px] font-bold bg-white/15 text-white px-2 py-1 rounded" data-testid={`shop-photo-cover-${i}`}>Cover</button>}
              <button type="button" onClick={() => removeAt(i)} title="Remove" className="w-7 h-7 rounded-full bg-red-500/90 text-white flex items-center justify-center" data-testid={`shop-photo-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
        <label className="aspect-square rounded-xl border-2 border-dashed border-gold/30 bg-vault flex items-center justify-center cursor-pointer hover:border-gold/60 transition-colors text-center text-slate-500">
          <div><Upload className="w-6 h-6 mx-auto mb-1" /><p className="text-[10px]">{busy ? "Uploading…" : "Add photo"}</p></div>
          <input type="file" accept="image/*" multiple hidden onChange={(e) => e.target.files.length && up(e.target.files)} data-testid="shop-image-upload-input" />
        </label>
      </div>
      <p className="text-[10px] text-slate-500 mt-1.5">Add multiple photos — the first one (Cover) is shown in the shop grid. Hover a photo to remove it or make it the cover.</p>
    </div>
  );
}

export default function HostDashboard() {
  const [tab, setTab] = useState("drop");
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-display text-3xl font-black text-white">Host Dashboard</h1>
      <p className="text-slate-400 mt-1">Create drops & shop listings, run draws, and fulfill orders.</p>
      <div className="mt-6 flex gap-2 overflow-x-auto custom-scrollbar pb-1" data-testid="host-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`host-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${tab === t.key ? "gold-gradient text-obsidian" : "card-surface text-slate-300"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "drop" && <DropForm />}
        {tab === "shop" && <ShopForm />}
        {tab === "case" && <CaseForm />}
        {tab === "orders" && <OrdersManager />}
        {tab === "credit" && <CreditForm />}
        {tab === "manage" && <ManageData />}
      </div>
    </div>
  );
}

function DropForm() {
  const [f, setF] = useState({ title: "", description: "", certificate_number: "", grade: "", brand: "", ticket_price: 5, total_seats: 50, num_winners: 1, max_per_user: "", free_seats: 0, is_referral: false, is_giveaway: false, non_winners_only: false, scheduled_at: "", save_as_template: false });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const set = (k, v) => setF({ ...f, [k]: v });
  const freePrice = f.is_giveaway || f.is_referral;

  const loadTemplates = () => api.get("/drop-templates").then(({ data }) => setTemplates(data)).catch(() => {});
  useEffect(() => { loadTemplates(); }, []);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setF({
      title: t.title || "", description: t.description || "", certificate_number: t.certificate_number || "",
      grade: t.grade || "", brand: t.brand || "", ticket_price: t.ticket_price ?? 5,
      total_seats: t.total_seats || 50, num_winners: t.num_winners || 1,
      max_per_user: t.max_per_user || "", free_seats: t.free_seats || 0,
      is_referral: t.is_referral || false, is_giveaway: t.is_giveaway || false, non_winners_only: t.non_winners_only || false, scheduled_at: "", save_as_template: false,
    });
    setPhoto(t.prize_photo || null);
    toast.success(`Loaded template "${t.name}"`);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/drops", {
        ...f, prize_photo: photo,
        ticket_price: freePrice ? 0 : (parseFloat(f.ticket_price) || 0),
        total_seats: parseInt(f.total_seats), num_winners: parseInt(f.num_winners),
        max_per_user: f.max_per_user ? parseInt(f.max_per_user) : null,
        free_seats: parseInt(f.free_seats) || 0,
        scheduled_at: f.scheduled_at ? new Date(f.scheduled_at).toISOString() : null,
      });
      if (f.save_as_template) {
        try {
          await api.post("/drop-templates", {
            name: f.title || "Untitled template", title: f.title, description: f.description, prize_photo: photo,
            certificate_number: f.certificate_number, grade: f.grade, brand: f.brand,
            ticket_price: freePrice ? 0 : (parseFloat(f.ticket_price) || 0),
            total_seats: parseInt(f.total_seats) || 50, num_winners: parseInt(f.num_winners) || 1,
            max_per_user: f.max_per_user ? parseInt(f.max_per_user) : null,
            free_seats: parseInt(f.free_seats) || 0, is_referral: f.is_referral, is_giveaway: f.is_giveaway, non_winners_only: f.non_winners_only,
          });
          loadTemplates();
        } catch { /* template save is best-effort */ }
      }
      toast.success(f.scheduled_at ? "Drop scheduled!" : "Drop created!");
      setF({ title: "", description: "", certificate_number: "", grade: "", brand: "", ticket_price: 5, total_seats: 50, num_winners: 1, max_per_user: "", free_seats: 0, is_referral: false, is_giveaway: false, non_winners_only: false, scheduled_at: "", save_as_template: false });
      setPhoto(null);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="card-surface rounded-2xl p-6 space-y-4" data-testid="create-drop-form">
      {templates.length > 0 && (
        <div className="flex items-center gap-2 bg-vault/60 rounded-xl p-3" data-testid="template-bar">
          <Bookmark className="w-4 h-4 text-gold shrink-0" />
          <select className={`${inp} flex-1`} defaultValue="" onChange={(e) => { if (e.target.value) applyTemplate(e.target.value); e.target.value = ""; }} data-testid="template-select">
            <option value="">Start from a saved template…</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      )}
      <ImageUpload value={photo} onChange={setPhoto} />
      <input className={inp} placeholder="Card title (e.g. Charizard Base Set PSA 10)" value={f.title} onChange={(e) => set("title", e.target.value)} required data-testid="drop-title-input" />
      <textarea className={inp} rows={3} placeholder="Description" value={f.description} onChange={(e) => set("description", e.target.value)} />
      <div className="grid grid-cols-3 gap-4">
        <select className={inp} value={f.brand || ""} onChange={(e) => set("brand", e.target.value)} data-testid="drop-brand-select">
          <option value="">Company</option>
          {SHOP_BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <input className={inp} placeholder="Grade (e.g. 10)" value={f.grade} onChange={(e) => set("grade", e.target.value)} />
        <input className={inp} placeholder="Certificate #" value={f.certificate_number} onChange={(e) => set("certificate_number", e.target.value)} />
      </div>
      <div>
        <p className="text-xs text-slate-400 mb-2">Drop type</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className={`flex items-center gap-3 rounded-xl p-3 cursor-pointer border transition-colors ${f.is_giveaway ? "border-gold bg-gold/10" : "card-surface border-gold/15"}`}>
            <input type="checkbox" checked={f.is_giveaway} onChange={(e) => set("is_giveaway", e.target.checked)} className="w-5 h-5 accent-yellow-500" data-testid="drop-giveaway-checkbox" />
            <span className="text-sm text-white flex items-center gap-2"><Gift className="w-4 h-4 text-gold" /> Free giveaway (no cost to enter)</span>
          </label>
          <label className={`flex items-center gap-3 rounded-xl p-3 cursor-pointer border transition-colors ${f.is_referral ? "border-gold bg-gold/10" : "card-surface border-gold/15"}`}>
            <input type="checkbox" checked={f.is_referral} onChange={(e) => set("is_referral", e.target.checked)} className="w-5 h-5 accent-yellow-500" data-testid="drop-referral-checkbox" />
            <span className="text-sm text-white flex items-center gap-2"><UserPlus className="w-4 h-4 text-gold" /> Referral giveaway (entries via invites)</span>
          </label>
          <label className={`flex items-center gap-3 rounded-xl p-3 cursor-pointer border transition-colors sm:col-span-2 ${f.non_winners_only ? "border-gold bg-gold/10" : "card-surface border-gold/15"}`}>
            <input type="checkbox" checked={f.non_winners_only} onChange={(e) => set("non_winners_only", e.target.checked)} className="w-5 h-5 accent-yellow-500" data-testid="drop-nonwinners-checkbox" />
            <span className="text-sm text-white flex items-center gap-2"><Trophy className="w-4 h-4 text-gold" /> Non-winners only (only members who haven't won before can enter)</span>
          </label>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div><label className="text-xs text-slate-400">Seat price ($)</label><input type="number" step="0.01" min="0" className={`${inp} ${freePrice ? "opacity-50" : ""}`} value={freePrice ? 0 : f.ticket_price} disabled={freePrice} onChange={(e) => set("ticket_price", e.target.value)} data-testid="drop-price-input" /><p className="text-[10px] text-slate-500 mt-1">{freePrice ? "Free to enter" : "Paid seats"}</p></div>
        <div><label className="text-xs text-slate-400">Total seats</label><input type="number" min="1" className={inp} value={f.total_seats} onChange={(e) => set("total_seats", e.target.value)} required data-testid="drop-seats-input" /></div>
        <div><label className="text-xs text-slate-400"># Winners</label><input type="number" min="1" className={inp} value={f.num_winners} onChange={(e) => set("num_winners", e.target.value)} data-testid="drop-winners-input" /></div>
        <div><label className="text-xs text-slate-400">Max per user</label><input type="number" min="1" className={inp} placeholder="∞" value={f.max_per_user} onChange={(e) => set("max_per_user", e.target.value)} /></div>
      </div>
      {!freePrice && <div><label className="text-xs text-slate-400">Hidden free seats</label><input type="number" min="0" className={inp} value={f.free_seats} onChange={(e) => set("free_seats", e.target.value)} /><p className="text-[10px] text-slate-500 mt-1">Randomly mark this many seats free — hidden from buyers; they aren't charged if they land one.</p></div>}
      {!freePrice && <PayoutEstimate price={f.ticket_price} seats={f.total_seats} free={f.free_seats} />}
      <div>
        <label className="text-xs text-slate-400 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-gold" /> Schedule go-live (optional)</label>
        <input type="datetime-local" className={inp} value={f.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} data-testid="drop-schedule-input" />
        <p className="text-[10px] text-slate-500 mt-1">{f.scheduled_at ? "Drop stays hidden until this time, then publishes automatically." : "Leave empty to publish immediately."}</p>
      </div>
      <label className={`flex items-center gap-3 rounded-xl p-3 cursor-pointer border transition-colors ${f.save_as_template ? "border-gold bg-gold/10" : "card-surface border-gold/15"}`}>
        <input type="checkbox" checked={f.save_as_template} onChange={(e) => set("save_as_template", e.target.checked)} className="w-5 h-5 accent-yellow-500" data-testid="drop-template-checkbox" />
        <span className="text-sm text-white flex items-center gap-2"><Bookmark className="w-4 h-4 text-gold" /> Save as template (reuse this drop later)</span>
      </label>
      <button disabled={busy} className="w-full gold-gradient text-obsidian font-bold py-3 rounded-xl flex items-center justify-center gap-2" data-testid="submit-drop"><Plus className="w-5 h-5" /> {busy ? "Saving…" : (f.scheduled_at ? "Schedule Drop" : "Create Drop")}</button>
    </form>
  );
}

function ShopForm() {
  const [f, setF] = useState({ title: "", description: "", price: 100, certificate_number: "", grade: "", brand: "" });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/shop", { ...f, photos, photo: photos[0] || null, price: parseFloat(f.price) });
      toast.success("Shop item listed!");
      setF({ title: "", description: "", price: 100, certificate_number: "", grade: "", brand: "" }); setPhotos([]);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="card-surface rounded-2xl p-6 space-y-4" data-testid="create-shop-form">
      <MultiImageUpload values={photos} onChange={setPhotos} />
      <input className={inp} placeholder="Card title" value={f.title} onChange={(e) => set("title", e.target.value)} required data-testid="shop-title-input" />
      <textarea className={inp} rows={3} placeholder="Description" value={f.description} onChange={(e) => set("description", e.target.value)} />
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-slate-400">Grading company</label>
          <select className={inp} value={f.brand} onChange={(e) => set("brand", e.target.value)} data-testid="shop-brand-select">
            <option value="">— Select —</option>
            {SHOP_BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div><label className="text-xs text-slate-400">Price ($)</label><input type="number" step="0.01" min="0.5" className={inp} value={f.price} onChange={(e) => set("price", e.target.value)} required data-testid="shop-price-input" /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <input className={inp} placeholder="Grade (e.g. 10)" value={f.grade} onChange={(e) => set("grade", e.target.value)} />
        <input className={inp} placeholder="Cert #" value={f.certificate_number} onChange={(e) => set("certificate_number", e.target.value)} />
      </div>
      <button disabled={busy} className="w-full gold-gradient text-obsidian font-bold py-3 rounded-xl flex items-center justify-center gap-2" data-testid="submit-shop"><Plus className="w-5 h-5" /> {busy ? "Listing…" : "List Item"}</button>
    </form>
  );
}

function CreditForm() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState(10);
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");

  const load = useCallback(() => api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/wallet/grant", { email, amount: parseFloat(amount) }); toast.success(data.message); setEmail(""); setAmount(10); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const quickGrant = async (u) => {
    try { const { data } = await api.post("/wallet/grant", { email: u.email, amount: parseFloat(amount) || 0 }); toast.success(data.message); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const filtered = users.filter((u) => `${u.name} ${u.email} ${u.full_name}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6">
      <DuplicateAccounts onChange={load} />
      <form onSubmit={submit} className="card-surface rounded-2xl p-6 space-y-4 max-w-md" data-testid="grant-credit-form">
        <h3 className="font-display font-bold text-white flex items-center gap-2"><Gift className="w-5 h-5 text-gold" /> Grant store credit</h3>
        <input className={inp} type="email" placeholder="Player email (or pick below)" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="grant-email" />
        <div><label className="text-xs text-slate-400">Amount ($)</label><input className={inp} type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid="grant-amount" /></div>
        <button disabled={busy} className="w-full gold-gradient text-obsidian font-bold py-3 rounded-xl" data-testid="submit-grant">{busy ? "Granting…" : "Grant Credit"}</button>
      </form>

      <div className="card-surface rounded-2xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
          <h3 className="font-display font-bold text-white flex items-center gap-2 shrink-0"><Users className="w-5 h-5 text-gold" /> All users</h3>
          <input className={`${inp} w-full sm:max-w-[220px]`} placeholder="Search name / email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="user-search" />
        </div>
        <div className="space-y-2 max-h-[440px] overflow-y-auto custom-scrollbar" data-testid="user-list">
          {filtered.map((u) => (
            <div key={u.id} className="bg-vault/60 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`user-row-${u.id}`}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-full gold-gradient flex items-center justify-center text-obsidian font-bold shrink-0">{u.name?.[0]?.toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><span className="text-white font-semibold text-sm truncate">{u.name}</span>{u.role === "admin" && <span className="text-[9px] font-mono font-bold px-1.5 rounded gold-gradient text-obsidian shrink-0">HOST</span>}{u.banned && <span className="text-[9px] font-mono font-bold px-1.5 rounded bg-red-500/20 text-red-300 shrink-0">SUSPENDED</span>}</div>
                  {u.full_name && <p className="text-slate-300 text-xs truncate">{u.full_name}</p>}
                  <p className="text-slate-500 text-xs truncate">{u.email}</p>
                </div>
                <span className="font-mono text-gold text-sm shrink-0">${u.store_credit.toFixed(2)}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => nav(`/messages/${u.id}`)} className="flex-1 sm:flex-none text-xs card-surface border border-gold/20 rounded-lg px-3 py-2 text-slate-200 flex items-center justify-center gap-1.5" data-testid={`message-user-${u.id}`}><MessageSquare className="w-3.5 h-3.5 text-gold" /> Message</button>
                <button onClick={() => setEmail(u.email)} className="flex-1 sm:flex-none text-xs card-surface border border-gold/20 rounded-lg px-3 py-2 text-slate-200" data-testid={`select-user-${u.id}`}>Select</button>
                <button onClick={() => quickGrant(u)} className="flex-1 sm:flex-none text-xs gold-gradient text-obsidian font-bold rounded-lg px-3 py-2 whitespace-nowrap" data-testid={`grant-user-${u.id}`}>+${(parseFloat(amount) || 0)}</button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <p className="text-slate-500 text-sm text-center py-6">No users found.</p>}
        </div>
      </div>
    </div>
  );
}

function DuplicateAccounts({ onChange }) {
  const [data, setData] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const load = useCallback(() => api.get("/users/duplicates").then(({ data }) => setData(data)).catch(() => setData({ groups: [], hidden: [] })), []);
  useEffect(() => { load(); }, [load]);
  const toggleBan = async (u) => {
    if (!u.banned) {
      const typed = window.prompt(`Suspend ${u.name} (${u.email})?\n\nThis removes all their entries from open drops (paid seats refunded as store credit). Removed entries can't be put back.\n\nType SUSPEND to confirm:`);
      if (typed === null) return;
      if (typed.trim().toUpperCase() !== "SUSPEND") { toast.error("Not suspended — you didn't type SUSPEND"); return; }
    }
    try { const { data } = await api.post(`/users/${u.id}/ban`, { banned: !u.banned, remove_entries: !u.banned }); toast.success(data.message); load(); onChange?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setIgnored = async (g, ignore) => {
    try { const { data } = await api.post(`/users/duplicates/${ignore ? "ignore" : "unignore"}`, { user_ids: g.users.map((u) => u.id) }); toast.success(data.message); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (data === null) return null;
  const { groups, hidden } = data;
  return (
    <div className="rounded-2xl p-5 border border-amber-500/30 bg-amber-500/5" data-testid="duplicate-accounts">
      <h3 className="font-display font-bold text-white flex items-center gap-2 mb-1"><AlertTriangle className="w-5 h-5 text-amber-400" /> Possible duplicate accounts ({groups.length})</h3>
      <p className="text-slate-400 text-xs mb-3">Accounts sharing a device or shipping address. If they're real people (like family), tap "Not a duplicate" so they're never flagged again.</p>
      {groups.length === 0 ? <p className="text-slate-500 text-sm text-center py-3" data-testid="no-duplicates">No duplicates found.</p> : (
        <div className="space-y-3 max-h-[420px] overflow-y-auto custom-scrollbar">
          {groups.map((g, gi) => <DupGroup key={gi} g={g} gi={gi} onBan={toggleBan} onIgnore={() => setIgnored(g, true)} />)}
        </div>
      )}
      {hidden.length > 0 && (
        <div className="mt-3">
          <button onClick={() => setShowHidden((s) => !s)} className="text-xs text-slate-400 hover:text-white underline" data-testid="toggle-hidden-dups">{showHidden ? "Hide" : "Show"} {hidden.length} marked "not a duplicate"</button>
          {showHidden && (
            <div className="space-y-2 mt-2" data-testid="hidden-dups">
              {hidden.map((g, gi) => (
                <div key={gi} className="bg-vault/40 rounded-xl p-3 flex items-center gap-3" data-testid={`hidden-dup-${gi}`}>
                  <p className="flex-1 min-w-0 text-xs text-slate-300 truncate"><span className="text-emerald-400 font-semibold">Approved</span> · {g.users.map((u) => u.name).join(" & ")}</p>
                  <button onClick={() => setIgnored(g, false)} className="text-xs card-surface border border-gold/20 rounded-lg px-3 py-1.5 text-slate-200 shrink-0" data-testid={`unignore-dup-${gi}`}>Flag again</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DupGroup({ g, gi, onBan, onIgnore }) {
  return (
    <div className="bg-vault/60 rounded-xl p-3" data-testid={`dup-group-${gi}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-[10px] font-mono font-bold uppercase tracking-wide text-amber-300">{g.reason} · {g.users.length} accounts</p>
        <button onClick={onIgnore} className="text-[11px] font-semibold text-emerald-300 border border-emerald-400/30 rounded-lg px-2.5 py-1 hover:bg-emerald-500/10 shrink-0" data-testid={`ignore-dup-${gi}`}>Not a duplicate</button>
      </div>
      <div className="space-y-2">
        {g.users.map((u) => (
          <div key={u.id} className="flex items-center gap-3" data-testid={`dup-user-${gi}-${u.id}`}>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold truncate ${u.banned ? "text-slate-500 line-through" : "text-white"}`}>{u.name}{u.full_name ? ` · ${u.full_name}` : ""}</p>
              <p className="text-slate-500 text-xs truncate">{u.email}</p>
            </div>
            {u.role === "admin" ? <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded gold-gradient text-obsidian shrink-0" data-testid={`dup-host-${gi}`}>HOST</span> : <button onClick={() => onBan(u)} className={`text-xs font-semibold rounded-lg px-3 py-1.5 shrink-0 ${u.banned ? "card-surface border border-emerald-400/30 text-emerald-300" : "bg-red-500/15 border border-red-500/30 text-red-300"}`} data-testid={`ban-user-${gi}-${u.id}`}>{u.banned ? "Restore" : "Suspend & remove"}</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function NotifReminderCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/notif-reminder/status").then(({ data }) => setStatus(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    if (!window.confirm("Email every player a reminder to turn on notifications now?")) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/notif-reminder/send"); toast.success(data.message); setTimeout(load, 1500); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const last = status?.last;
  return (
    <div className="card-surface rounded-2xl p-5" data-testid="notif-reminder-card">
      <h3 className="font-display font-bold text-white flex items-center gap-2"><BellRing className="w-5 h-5 text-gold" /> Notification reminder email</h3>
      <p className="text-slate-400 text-xs mt-1">Sent automatically to every player on the 1st of each month (~10am ET). Players can unsubscribe from the email.</p>
      <div className="flex items-center gap-3 mt-3 flex-wrap">
        <button onClick={send} disabled={busy} className="gold-gradient text-obsidian font-bold text-sm rounded-xl px-4 py-2 disabled:opacity-50" data-testid="notif-reminder-send">{busy ? "Sending…" : "Send now"}</button>
        <p className="text-[11px] text-slate-500" data-testid="notif-reminder-status">
          {last ? `Last: ${new Date(last.started_at).toLocaleDateString()} · ${last.status === "done" ? `${last.sent} sent` : "sending…"}` : "Not sent yet"}{status?.opted_out ? ` · ${status.opted_out} unsubscribed` : ""}
        </p>
      </div>
    </div>
  );
}

function ManageData() {
  const [drops, setDrops] = useState(null);
  const [shop, setShop] = useState(null);
  const [cases, setCases] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editCase, setEditCase] = useState(null);
  const [editDrop, setEditDrop] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [hero, setHero] = useState("");
  const load = useCallback(() => {
    api.get("/drops").then(({ data }) => setDrops(data)).catch(() => setDrops([]));
    api.get("/shop").then(({ data }) => setShop(data)).catch(() => setShop([]));
    api.get("/cases").then(({ data }) => setCases(data)).catch(() => setCases([]));
    api.get("/drop-templates").then(({ data }) => setTemplates(data)).catch(() => setTemplates([]));
    api.get("/settings").then(({ data }) => setHero(data.hero_image || "")).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveHero = async (image) => {
    try { const { data } = await api.put("/settings/hero", { image: image || "" }); setHero(data.hero_image || ""); toast.success(image ? "Home image updated" : "Reverted to auto image"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveDropTemplate = async (d) => {
    const name = window.prompt("Name this template:", d.title);
    if (!name) return;
    try {
      await api.post("/drop-templates", {
        name, title: d.title, description: d.description || "", prize_photo: d.prize_photo,
        certificate_number: d.certificate_number || "", grade: d.grade || "", brand: d.brand || "",
        ticket_price: d.ticket_price || 0, total_seats: d.total_seats || 50, num_winners: d.num_winners || 1,
        max_per_user: d.max_per_user || null, free_seats: d.free_seats || 0,
        is_referral: d.is_referral || false, is_giveaway: (d.ticket_price || 0) === 0 && !d.is_referral, non_winners_only: d.non_winners_only || false,
      });
      toast.success("Saved as template"); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/drop-templates/${t.id}`); toast.success("Template deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const delDrop = async (d) => {
    if (!window.confirm(`Delete drop "${d.title}"? This permanently removes it and its orders.`)) return;
    try { await api.delete(`/drops/${d.id}`); toast.success("Drop deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reopenDrop = async (d) => {
    if (!window.confirm(`Reopen "${d.title}" so new players can join the open seats?`)) return;
    try { const { data } = await api.post(`/drops/${d.id}/reopen`); toast.success(data.message); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const cancelDrop = async (d) => {
    if (!window.confirm(`Cancel "${d.title}" and refund all paid entries to store credit?`)) return;
    try { const { data } = await api.post(`/drops/${d.id}/cancel`); toast.success(data.message); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delShop = async (s) => {
    if (!window.confirm(`Delete shop item "${s.title}"?`)) return;
    try { await api.delete(`/shop/${s.id}`); toast.success("Item deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delCase = async (c) => {
    if (!window.confirm(`Delete case "${c.title}"?`)) return;
    try { await api.delete(`/cases/${c.id}`); toast.success("Case deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggleColor = async (c, idx) => {
    const colors = c.colors.map((cc, i) => i === idx ? { ...cc, in_stock: !cc.in_stock } : cc);
    try { await api.patch(`/cases/${c.id}`, { title: c.title, description: c.description, photo: c.photo, price: c.price, colors }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const clearChat = async () => {
    if (!window.confirm("Clear ALL live chat messages? This cannot be undone.")) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/clear-chat"); toast.success(data.message); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (drops === null || shop === null || cases === null) return <Loader />;

  const liveDrops = drops.filter((d) => d.status !== "drawn");

  return (
    <div className="space-y-6" data-testid="manage-data">
      <NotifReminderCard />
      <div className="card-surface rounded-2xl p-5">
        <h3 className="font-display font-bold text-white flex items-center gap-2 mb-1"><Store className="w-5 h-5 text-gold" /> Home hero image</h3>
        <p className="text-slate-500 text-xs mb-3">The large image shown on the home screen. Upload one to override the automatic image.</p>
        <div className="max-w-xs">
          <ImageUpload value={hero} onChange={saveHero} />
        </div>
        {hero && <button onClick={() => saveHero("")} className="mt-2 text-xs text-slate-400 hover:text-white underline" data-testid="hero-reset">Reset to automatic image</button>}
      </div>

      <div className="card-surface rounded-2xl p-5">
        <h3 className="font-display font-bold text-white flex items-center gap-2 mb-3"><Zap className="w-5 h-5 text-gold" /> Drops ({liveDrops.length})</h3>
        {liveDrops.length === 0 ? <p className="text-slate-500 text-sm py-4 text-center">No drops.</p> : (
          <div className="space-y-2 max-h-[320px] overflow-y-auto custom-scrollbar">
            {liveDrops.map((d) => (
              <div key={d.id} className="flex items-center gap-3 bg-vault/60 rounded-xl p-3" data-testid={`manage-drop-${d.id}`}>
                <div className="w-10 h-10 rounded-lg bg-obsidian overflow-hidden shrink-0">{d.prize_photo ? <img src={fileUrl(d.prize_photo)} alt="" className="w-full h-full object-cover" /> : null}</div>
                <div className="flex-1 min-w-0"><p className="text-white text-sm font-semibold truncate">{d.title}</p><p className="text-slate-400 text-xs">{d.status === "scheduled" && d.scheduled_at ? `⏱ Goes live ${new Date(d.scheduled_at).toLocaleString()}` : `${d.status} · ${d.claimed_seats}/${d.total_seats} seats`}</p></div>
                <button onClick={() => setEditDrop(d)} className="text-gold hover:text-gold/80 p-2 rounded-lg hover:bg-gold/10" data-testid={`edit-drop-${d.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => saveDropTemplate(d)} title="Save as template" className="text-slate-300 hover:text-white p-2 rounded-lg hover:bg-white/5" data-testid={`template-drop-${d.id}`}><Bookmark className="w-4 h-4" /></button>
                {(d.status === "drawing" || d.status === "full") && d.claimed_seats < d.total_seats && <button onClick={() => reopenDrop(d)} title="Reopen for new entries" className="text-emerald-400 hover:text-emerald-300 text-xs font-semibold px-2 py-1.5 rounded-lg border border-emerald-400/30 hover:bg-emerald-500/10" data-testid={`reopen-drop-${d.id}`}>Reopen</button>}
                {(d.status === "active" || d.status === "scheduled") && <button onClick={() => cancelDrop(d)} title="Cancel & refund entries" className="text-amber-400 hover:text-amber-300 p-2 rounded-lg hover:bg-amber-500/10" data-testid={`cancel-drop-${d.id}`}><Ban className="w-4 h-4" /></button>}
                <button onClick={() => delDrop(d)} className="text-red-400 hover:text-red-300 p-2 rounded-lg hover:bg-red-500/10" data-testid={`delete-drop-${d.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card-surface rounded-2xl p-5">
        <h3 className="font-display font-bold text-white flex items-center gap-2 mb-1"><Bookmark className="w-5 h-5 text-gold" /> Drop templates ({templates.length})</h3>
        <p className="text-slate-500 text-xs mb-3">Reusable drop setups. Load one from the "New Drop" tab to host it again.</p>
        {templates.length === 0 ? <p className="text-slate-500 text-sm py-4 text-center">No templates yet. Save one from the New Drop tab or the bookmark icon above.</p> : (
          <div className="space-y-2 max-h-[280px] overflow-y-auto custom-scrollbar">
            {templates.map((t) => (
              <div key={t.id} className="flex items-center gap-3 bg-vault/60 rounded-xl p-3" data-testid={`manage-template-${t.id}`}>
                <div className="w-10 h-10 rounded-lg bg-obsidian overflow-hidden shrink-0">{t.prize_photo ? <img src={fileUrl(t.prize_photo)} alt="" className="w-full h-full object-cover" /> : <Bookmark className="w-4 h-4 text-slate-600 m-auto mt-3" />}</div>
                <div className="flex-1 min-w-0"><p className="text-white text-sm font-semibold truncate">{t.name}</p><p className="text-slate-400 text-xs truncate">{t.is_referral ? "Referral" : (t.ticket_price ? `$${t.ticket_price} · ` : "Free · ")}{t.total_seats} seats · {t.num_winners} winner{t.num_winners > 1 ? "s" : ""}</p></div>
                <button onClick={() => delTemplate(t)} className="text-red-400 hover:text-red-300 p-2 rounded-lg hover:bg-red-500/10" data-testid={`delete-template-${t.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card-surface rounded-2xl p-5">
        <h3 className="font-display font-bold text-white flex items-center gap-2 mb-3"><Store className="w-5 h-5 text-gold" /> Shop items ({shop.length})</h3>
        {shop.length === 0 ? <p className="text-slate-500 text-sm py-4 text-center">No shop items.</p> : (
          <div className="space-y-2 max-h-[320px] overflow-y-auto custom-scrollbar">
            {shop.map((s) => (
              <div key={s.id} className="flex items-center gap-3 bg-vault/60 rounded-xl p-3" data-testid={`manage-shop-${s.id}`}>
                <div className="w-10 h-10 rounded-lg bg-obsidian overflow-hidden shrink-0">{s.photo ? <img src={fileUrl(s.photo)} alt="" className="w-full h-full object-cover" /> : null}</div>
                <div className="flex-1 min-w-0"><p className="text-white text-sm font-semibold truncate">{s.title}</p><p className="text-slate-400 text-xs">${s.price?.toFixed(2)} · {s.status}</p></div>
                <button onClick={() => setEditItem(s)} className="text-gold hover:text-gold/80 p-2 rounded-lg hover:bg-gold/10" data-testid={`edit-shop-${s.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => delShop(s)} className="text-red-400 hover:text-red-300 p-2 rounded-lg hover:bg-red-500/10" data-testid={`delete-shop-${s.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card-surface rounded-2xl p-5">
        <h3 className="font-display font-bold text-white flex items-center gap-2 mb-3"><Box className="w-5 h-5 text-gold" /> Slab cases ({cases.length})</h3>
        {cases.length === 0 ? <p className="text-slate-500 text-sm py-4 text-center">No cases.</p> : (
          <div className="space-y-3 max-h-[380px] overflow-y-auto custom-scrollbar">
            {cases.map((c) => (
              <div key={c.id} className="bg-vault/60 rounded-xl p-3" data-testid={`manage-case-${c.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-obsidian overflow-hidden shrink-0">{c.photo ? <img src={fileUrl(c.photo)} alt="" className="w-full h-full object-cover" /> : null}</div>
                  <div className="flex-1 min-w-0"><p className="text-white text-sm font-semibold truncate">{c.title}</p><p className="text-slate-400 text-xs">${c.price?.toFixed(2)} · {c.colors?.length || 0} colors</p></div>
                  <button onClick={() => setEditCase(c)} className="text-gold hover:text-gold/80 p-2 rounded-lg hover:bg-gold/10" data-testid={`edit-case-${c.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => delCase(c)} className="text-red-400 hover:text-red-300 p-2 rounded-lg hover:bg-red-500/10" data-testid={`delete-case-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(c.colors || []).map((col, idx) => (
                    <button key={idx} onClick={() => toggleColor(c, idx)} data-testid={`toggle-color-${c.id}-${idx}`}
                      className={`text-[11px] font-semibold px-2.5 py-1 rounded-lg border transition-colors ${col.in_stock ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-300" : "border-slate-500/30 bg-slate-500/10 text-slate-400 line-through"}`}>
                      {col.name} · {col.in_stock ? "In stock" : "Out"}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-slate-500 mt-1.5">Tap a color to toggle its stock.</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl p-5 border border-red-500/30 bg-red-500/5">
        <h3 className="font-display font-bold text-white flex items-center gap-2 mb-1"><AlertTriangle className="w-5 h-5 text-red-400" /> Danger zone</h3>
        <p className="text-slate-400 text-xs mb-4">Use these to clean up test data on the live site. Accounts are always kept.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={clearChat} disabled={busy} className="flex-1 card-surface border border-gold/20 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 hover:border-gold/40 disabled:opacity-50" data-testid="clear-chat-button"><MessageSquare className="w-4 h-4 text-gold" /> Clear all chat</button>
        </div>
      </div>
      {editItem && <EditShopModal item={editItem} onClose={() => setEditItem(null)} onSaved={() => { setEditItem(null); load(); }} />}
      {editCase && <EditCaseModal caseItem={editCase} onClose={() => setEditCase(null)} onSaved={() => { setEditCase(null); load(); }} />}
      {editDrop && <EditDropModal drop={editDrop} onClose={() => setEditDrop(null)} onSaved={() => { setEditDrop(null); load(); }} />}
    </div>
  );
}

function EditCaseModal({ caseItem, onClose, onSaved }) {
  const [f, setF] = useState({ title: caseItem.title || "", description: caseItem.description || "", price: caseItem.price || 0 });
  const [photo, setPhoto] = useState(caseItem.photo || null);
  const [colors, setColors] = useState((caseItem.colors && caseItem.colors.length ? caseItem.colors : [{ name: "", in_stock: true }]).map((c) => ({ ...c })));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const setColor = (i, k, v) => setColors(colors.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const save = async () => {
    const cleaned = colors.filter((c) => c.name.trim()).map((c) => ({ name: c.name.trim(), in_stock: c.in_stock }));
    if (!cleaned.length) { toast.error("Add at least one color"); return; }
    setBusy(true);
    try { await api.patch(`/cases/${caseItem.id}`, { ...f, photo, price: parseFloat(f.price), colors: cleaned }); toast.success("Case updated"); onSaved(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="edit-case-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg card-surface rounded-2xl p-6 max-h-[90vh] overflow-y-auto custom-scrollbar">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold text-white">Edit case</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><span className="text-2xl leading-none">×</span></button>
        </div>
        <div className="space-y-4">
          <ImageUpload value={photo} onChange={setPhoto} />
          <input className={inp} placeholder="Case title" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="edit-case-title" />
          <textarea className={inp} rows={2} placeholder="Description" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="edit-case-desc" />
          <div><label className="text-xs text-slate-400">Price per case ($)</label><input type="number" step="0.01" min="0.5" className={inp} value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="edit-case-price" /></div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-slate-400">Colors</p>
              <button type="button" onClick={() => setColors([...colors, { name: "", in_stock: true }])} className="text-xs text-gold flex items-center gap-1" data-testid="edit-case-add-color"><Plus className="w-3.5 h-3.5" /> Add color</button>
            </div>
            <div className="space-y-2">
              {colors.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input className={inp} placeholder="Color name" value={c.name} onChange={(e) => setColor(i, "name", e.target.value)} data-testid={`edit-case-color-${i}`} />
                  <label className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border cursor-pointer whitespace-nowrap text-xs ${c.in_stock ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-300" : "border-gold/20 text-slate-400"}`}>
                    <input type="checkbox" checked={c.in_stock} onChange={(e) => setColor(i, "in_stock", e.target.checked)} className="accent-emerald-500" /> In stock
                  </label>
                  <button type="button" onClick={() => setColors(colors.filter((_, idx) => idx !== i))} className="text-slate-500 hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
          <button disabled={busy} onClick={save} className="w-full gold-gradient text-obsidian font-bold py-3 rounded-xl" data-testid="edit-case-save">{busy ? "Saving…" : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}

function EditShopModal({ item, onClose, onSaved }) {
  const [f, setF] = useState({
    title: item.title || "", description: item.description || "", price: item.price || 0,
    grade: item.grade || "", brand: item.brand || "", certificate_number: item.certificate_number || "",
  });
  const [photos, setPhotos] = useState(item.photos && item.photos.length ? item.photos : (item.photo ? [item.photo] : []));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/shop/${item.id}`, { ...f, photos, photo: photos[0] || null, price: parseFloat(f.price) });
      toast.success("Item updated");
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="edit-shop-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg card-surface rounded-2xl p-6 max-h-[90vh] overflow-y-auto custom-scrollbar">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold text-white">Edit item</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="edit-shop-close"><span className="text-2xl leading-none">×</span></button>
        </div>
        <div className="space-y-4">
          <MultiImageUpload values={photos} onChange={setPhotos} />
          <input className={inp} placeholder="Card title" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="edit-title-input" />
          <textarea className={inp} rows={3} placeholder="Description" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="edit-desc-input" />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400">Grading company</label>
              <select className={inp} value={f.brand} onChange={(e) => set("brand", e.target.value)} data-testid="edit-brand-select">
                <option value="">— Select —</option>
                {SHOP_BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div><label className="text-xs text-slate-400">Price ($)</label><input type="number" step="0.01" min="0.5" className={inp} value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="edit-price-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <input className={inp} placeholder="Grade (e.g. 10)" value={f.grade} onChange={(e) => set("grade", e.target.value)} data-testid="edit-grade-input" />
            <input className={inp} placeholder="Cert #" value={f.certificate_number} onChange={(e) => set("certificate_number", e.target.value)} data-testid="edit-cert-input" />
          </div>
          <button disabled={busy} onClick={save} className="w-full gold-gradient text-obsidian font-bold py-3 rounded-xl" data-testid="edit-save">{busy ? "Saving…" : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}

function EditDropModal({ drop, onClose, onSaved }) {
  const toLocalInput = (isoStr) => { if (!isoStr) return ""; const d = new Date(isoStr); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
  const [f, setF] = useState({
    title: drop.title || "", description: drop.description || "", certificate_number: drop.certificate_number || "",
    grade: drop.grade || "", brand: drop.brand || "", ticket_price: drop.ticket_price ?? 5,
    total_seats: drop.total_seats || 1, num_winners: drop.num_winners || 1,
    max_per_user: drop.max_per_user || "", free_seats: drop.free_seats || 0,
    is_referral: drop.is_referral || false, is_giveaway: drop.is_giveaway || false, non_winners_only: drop.non_winners_only || false,
    scheduled_at: toLocalInput(drop.scheduled_at),
  });
  const [photo, setPhoto] = useState(drop.prize_photo || null);
  const [busy, setBusy] = useState(false);
  const canSchedule = drop.status === "scheduled" || drop.status === "active";
  const set = (k, v) => setF({ ...f, [k]: v });
  const freePrice = f.is_giveaway || f.is_referral;
  const locked = (drop.claimed_seats || 0) > 0;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/drops/${drop.id}`, {
        ...f, prize_photo: photo,
        ticket_price: freePrice ? 0 : (parseFloat(f.ticket_price) || 0),
        total_seats: parseInt(f.total_seats), num_winners: parseInt(f.num_winners),
        max_per_user: f.max_per_user ? parseInt(f.max_per_user) : null,
        free_seats: parseInt(f.free_seats) || 0,
        scheduled_at: f.scheduled_at ? new Date(f.scheduled_at).toISOString() : null,
      });
      toast.success("Drop updated");
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="edit-drop-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg card-surface rounded-2xl p-6 max-h-[90vh] overflow-y-auto custom-scrollbar">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold text-white">Edit drop</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="edit-drop-close"><span className="text-2xl leading-none">×</span></button>
        </div>
        <div className="space-y-4">
          <ImageUpload value={photo} onChange={setPhoto} />
          <input className={inp} placeholder="Card title" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="edit-drop-title" />
          <textarea className={inp} rows={3} placeholder="Description" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="edit-drop-description" />
          <div className="grid grid-cols-3 gap-4">
            <select className={inp} value={f.brand || ""} onChange={(e) => set("brand", e.target.value)} data-testid="edit-drop-brand">
              <option value="">Company</option>
              {SHOP_BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <input className={inp} placeholder="Grade" value={f.grade} onChange={(e) => set("grade", e.target.value)} data-testid="edit-drop-grade" />
            <input className={inp} placeholder="Cert #" value={f.certificate_number} onChange={(e) => set("certificate_number", e.target.value)} data-testid="edit-drop-cert" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className={`flex items-center gap-3 rounded-xl p-3 cursor-pointer border transition-colors ${f.is_giveaway ? "border-gold bg-gold/10" : "card-surface border-gold/15"}`}>
              <input type="checkbox" checked={f.is_giveaway} onChange={(e) => set("is_giveaway", e.target.checked)} className="w-5 h-5 accent-yellow-500" data-testid="edit-drop-giveaway" />
              <span className="text-sm text-white flex items-center gap-2"><Gift className="w-4 h-4 text-gold" /> Free giveaway</span>
            </label>
            <label className={`flex items-center gap-3 rounded-xl p-3 cursor-pointer border transition-colors ${f.is_referral ? "border-gold bg-gold/10" : "card-surface border-gold/15"}`}>
              <input type="checkbox" checked={f.is_referral} onChange={(e) => set("is_referral", e.target.checked)} className="w-5 h-5 accent-yellow-500" data-testid="edit-drop-referral" />
              <span className="text-sm text-white flex items-center gap-2"><UserPlus className="w-4 h-4 text-gold" /> Referral giveaway</span>
            </label>
            <label className={`flex items-center gap-3 rounded-xl p-3 cursor-pointer border transition-colors sm:col-span-2 ${f.non_winners_only ? "border-gold bg-gold/10" : "card-surface border-gold/15"}`}>
              <input type="checkbox" checked={f.non_winners_only} onChange={(e) => set("non_winners_only", e.target.checked)} className="w-5 h-5 accent-yellow-500" data-testid="edit-drop-nonwinners" />
              <span className="text-sm text-white flex items-center gap-2"><Trophy className="w-4 h-4 text-gold" /> Non-winners only</span>
            </label>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div><label className="text-xs text-slate-400">Seat price ($)</label><input type="number" step="0.01" min="0" className={`${inp} ${freePrice ? "opacity-50" : ""}`} value={freePrice ? 0 : f.ticket_price} disabled={freePrice} onChange={(e) => set("ticket_price", e.target.value)} data-testid="edit-drop-price" /></div>
            <div><label className="text-xs text-slate-400">Total seats</label><input type="number" min="1" className={`${inp} ${locked ? "opacity-50" : ""}`} value={f.total_seats} disabled={locked} onChange={(e) => set("total_seats", e.target.value)} data-testid="edit-drop-seats" /></div>
            <div><label className="text-xs text-slate-400"># Winners</label><input type="number" min="1" className={inp} value={f.num_winners} onChange={(e) => set("num_winners", e.target.value)} data-testid="edit-drop-winners" /></div>
            <div><label className="text-xs text-slate-400">Max per user</label><input type="number" min="1" className={inp} placeholder="∞" value={f.max_per_user} onChange={(e) => set("max_per_user", e.target.value)} data-testid="edit-drop-maxper" /></div>
          </div>
          {!freePrice && <div><label className="text-xs text-slate-400">Hidden free seats</label><input type="number" min="0" className={`${inp} ${locked ? "opacity-50" : ""}`} value={f.free_seats} disabled={locked} onChange={(e) => set("free_seats", e.target.value)} data-testid="edit-drop-free" /></div>}
          {!freePrice && <PayoutEstimate price={f.ticket_price} seats={f.total_seats} free={f.free_seats} />}
          {canSchedule && (
            <div>
              <label className="text-xs text-slate-400 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-gold" /> Schedule go-live</label>
              <input type="datetime-local" className={inp} value={f.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} data-testid="edit-drop-schedule" />
              <p className="text-[10px] text-slate-500 mt-1">{f.scheduled_at ? "Hidden from players until this time, then publishes automatically." : "Empty = live now."}</p>
            </div>
          )}
          {locked && <p className="text-[11px] text-amber-400" data-testid="edit-drop-locked-note">Seats are already taken — you can edit the details, price, winners and max per user, but seat count and hidden free seats are locked.</p>}
          <button disabled={busy} onClick={save} className="w-full gold-gradient text-obsidian font-bold py-3 rounded-xl" data-testid="edit-drop-save">{busy ? "Saving…" : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}

function CaseForm() {
  const [f, setF] = useState({ title: "", description: "", price: 20 });
  const [photo, setPhoto] = useState(null);
  const [colors, setColors] = useState([{ name: "", in_stock: true }]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const setColor = (i, k, v) => setColors(colors.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const addColor = () => setColors([...colors, { name: "", in_stock: true }]);
  const removeColor = (i) => setColors(colors.filter((_, idx) => idx !== i));
  const submit = async (e) => {
    e.preventDefault();
    const cleaned = colors.filter((c) => c.name.trim()).map((c) => ({ name: c.name.trim(), in_stock: c.in_stock }));
    if (!cleaned.length) { toast.error("Add at least one color"); return; }
    setBusy(true);
    try {
      await api.post("/cases", { ...f, photo, price: parseFloat(f.price), colors: cleaned });
      toast.success("Case listed!");
      setF({ title: "", description: "", price: 20 }); setPhoto(null); setColors([{ name: "", in_stock: true }]);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="card-surface rounded-2xl p-6 space-y-4" data-testid="create-case-form">
      <ImageUpload value={photo} onChange={setPhoto} />
      <input className={inp} placeholder="Case title (e.g. Graded Slab Case — One Touch)" value={f.title} onChange={(e) => set("title", e.target.value)} required data-testid="case-title-input" />
      <textarea className={inp} rows={2} placeholder="Description" value={f.description} onChange={(e) => set("description", e.target.value)} />
      <div><label className="text-xs text-slate-400">Price per case ($)</label><input type="number" step="0.01" min="0.5" className={inp} value={f.price} onChange={(e) => set("price", e.target.value)} required data-testid="case-price-input" /></div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-slate-400">Colors</p>
          <button type="button" onClick={addColor} className="text-xs text-gold flex items-center gap-1" data-testid="case-add-color"><Plus className="w-3.5 h-3.5" /> Add color</button>
        </div>
        <div className="space-y-2">
          {colors.map((c, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`case-color-row-${i}`}>
              <input className={inp} placeholder="Color name (e.g. Black)" value={c.name} onChange={(e) => setColor(i, "name", e.target.value)} data-testid={`case-color-name-${i}`} />
              <label className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border cursor-pointer whitespace-nowrap text-xs ${c.in_stock ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-300" : "border-gold/20 text-slate-400"}`}>
                <input type="checkbox" checked={c.in_stock} onChange={(e) => setColor(i, "in_stock", e.target.checked)} className="accent-emerald-500" data-testid={`case-color-stock-${i}`} /> In stock
              </label>
              <button type="button" onClick={() => removeColor(i)} className="text-slate-500 hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
      <button disabled={busy} className="w-full gold-gradient text-obsidian font-bold py-3 rounded-xl flex items-center justify-center gap-2" data-testid="submit-case"><Plus className="w-5 h-5" /> {busy ? "Listing…" : "List Case"}</button>
    </form>
  );
}

function OrdersManager() {
  const [orders, setOrders] = useState(null);
  const [sub, setSub] = useState("pending");
  const [tracking, setTracking] = useState({});
  const load = useCallback(() => api.get("/orders/host").then(({ data }) => setOrders(data)).catch(() => setOrders([])), []);
  useEffect(() => { load(); }, [load]);

  const ship = async (id) => {
    const t = tracking[id];
    if (!t) { toast.error("Enter a tracking number"); return; }
    try { await api.post(`/orders/${id}/ship`, { tracking_number: t }); toast.success("Marked shipped"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const [editing, setEditing] = useState({});
  const cancelEdit = (id) => { const n = { ...editing }; delete n[id]; setEditing(n); };
  const saveTracking = async (id) => {
    const t = (editing[id] || "").trim();
    if (!t) { toast.error("Enter a tracking number"); return; }
    try { await api.patch(`/orders/${id}/tracking`, { tracking_number: t }); toast.success("Tracking updated"); cancelEdit(id); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (orders === null) return <Loader />;
  const filtered = orders.filter((o) => o.status === sub);

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["pending", "shipped"].map((s) => (
          <button key={s} onClick={() => setSub(s)} data-testid={`orders-subtab-${s}`} className={`px-4 py-2 rounded-lg text-sm font-semibold capitalize ${sub === s ? "gold-gradient text-obsidian" : "card-surface text-slate-300"}`}>{s} ({orders.filter((o) => o.status === s).length})</button>
        ))}
      </div>
      {filtered.length === 0 ? <div className="card-surface rounded-2xl p-12 text-center text-slate-400">No {sub} orders.</div> : (
        <div className="space-y-4" data-testid="host-orders-list">
          {filtered.map((o) => (
            <div key={o.id} className="card-surface rounded-2xl p-4" data-testid={`host-order-${o.id}`}>
              <div className="flex gap-4">
                <div className="w-16 h-16 rounded-xl bg-vault overflow-hidden shrink-0">{o.item_photo ? <img src={fileUrl(o.item_photo)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-600"><Package className="w-6 h-6" /></div>}</div>
                <div className="flex-1">
                  <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded ${o.type === "drop_win" ? "bg-gold/15 text-gold" : "bg-slate-500/15 text-slate-300"}`}>{o.type === "drop_win" ? "DROP WIN" : "SHOP"}</span>
                  <h3 className="font-display font-bold text-white mt-1">{o.item_title}</h3>
                  <p className="text-sm text-slate-400">{o.user_name} · {o.user_email}</p>
                  {o.ship_address ? (
                    <div className="text-sm text-slate-300 mt-2 flex items-start gap-1" data-testid={`ship-to-${o.id}`}>
                      <MapPin className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                      <span><b className="text-white">{o.ship_name}</b><br />{o.ship_address}</span>
                    </div>
                  ) : <p className="text-xs text-amber-400 mt-2">Waiting for buyer's shipping address…</p>}
                </div>
              </div>
              {sub === "pending" ? (
                <div className="mt-3 flex gap-2 border-t border-gold/15 pt-3">
                  <input className={inp} placeholder="Tracking number" value={tracking[o.id] || ""} onChange={(e) => setTracking({ ...tracking, [o.id]: e.target.value })} data-testid={`tracking-input-${o.id}`} />
                  <button onClick={() => ship(o.id)} className="gold-gradient text-obsidian font-bold px-4 rounded-xl flex items-center gap-2 whitespace-nowrap" data-testid={`ship-${o.id}`}><Truck className="w-4 h-4" /> Ship</button>
                </div>
              ) : editing[o.id] !== undefined ? (
                <div className="mt-3 flex gap-2 border-t border-gold/15 pt-3">
                  <input className={inp} autoFocus value={editing[o.id]} onChange={(e) => setEditing({ ...editing, [o.id]: e.target.value })} data-testid={`tracking-edit-input-${o.id}`} />
                  <button onClick={() => saveTracking(o.id)} className="gold-gradient text-obsidian font-bold px-4 rounded-xl whitespace-nowrap" data-testid={`tracking-save-${o.id}`}>Save</button>
                  <button onClick={() => cancelEdit(o.id)} className="card-surface text-slate-300 px-3 rounded-xl" data-testid={`tracking-cancel-${o.id}`}>Cancel</button>
                </div>
              ) : <p className="mt-2 text-sm text-emerald-400 flex items-center gap-1 flex-wrap"><Truck className="w-4 h-4" /> Tracking: <a href={trackingUrl(o.tracking_number)} target="_blank" rel="noopener noreferrer" className="font-mono underline underline-offset-2 hover:text-emerald-300 inline-flex items-center gap-1 break-all" data-testid={`tracking-link-${o.id}`}>{o.tracking_number} <ExternalLink className="w-3 h-3" /></a>
                  <button onClick={() => setEditing({ ...editing, [o.id]: o.tracking_number || "" })} className="ml-auto text-xs text-gold inline-flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-gold/10" data-testid={`tracking-edit-${o.id}`}><Pencil className="w-3 h-3" /> Edit</button></p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
